import * as React from "react";
import Modal from "../../Modal/Modal";
import Input from "../../Input-new/Input";
import { getData } from "../../../requests/CRUD";
import { executeQuery } from "../../../requests/executeQuery";
import { INVESTORS } from "../../../consts/tables";
import { GET_PROPERTY_OWNERSHIPS, GET_OWNERSHIP_INVESTORS } from "../../../consts/queries";
import { downloadDocumentWord } from "../../../requests/repAgreement";
import { shortDate, withEmbeddedImages } from "./RepAgreementForm";
import { formatName } from "../../../utils";

/**
 * Peak Agreement form + generator (Loan Info tab).
 *
 * The "Peak Agreement" button opens a data-entry modal (modeled on the LOI form
 * on the Listing Agent tab). On Generate, the entered values populate the firm's
 * standard "Capital Services Engagement Agreement": the property Name/Address are
 * woven into the Services paragraph, the Summary of Terms is filled from the form
 * fields, and the current date is inserted on the signature page. All other terms
 * are the fixed template language.
 *
 * Two outputs, as on the Rep Agreement tab: Generate opens the print dialog
 * (Save as PDF), and Word posts the same document to the backend converter
 * (App\Services\Documents\HtmlToDocx) for a real .docx. Both carry the same
 * name - see documentTitleFor.
 */

const DEFAULT_CONDITIONS =
    'The following are conditions precedent to closing:\n\n' +
    '1. Appraisal\n' +
    '2. Environmental Phase I ESA 2013 Standards\n' +
    '3. As-Built ALTA Survey 2016 Standards\n' +
    '4. Other conditions as Lender may determine';

// key -> label, in display order. `conditions` is rendered as a textarea.
const FIELDS = [
    { key: 'borrower', label: 'Borrower', doc: 'Borrower' },
    { key: 'creditFacilities', label: 'Credit Facilities', doc: 'Credit Facilities' },
    { key: 'purpose', label: 'Purpose', doc: 'Purpose' },
    { key: 'asset', label: 'Asset', doc: 'Asset' },
    { key: 'repayment', label: 'Repayment', doc: 'Repayment' },
    { key: 'rate', label: 'Rate', doc: 'Rate' },
    { key: 'term', label: 'Term', doc: 'Term' },
    { key: 'maturity', label: 'Maturity', doc: 'Maturity' },
    { key: 'amortization', label: 'Amortization', doc: 'Amortization' },
    { key: 'deposit', label: 'Deposit', doc: 'Deposit' },
    { key: 'bankLoanFee', label: 'Bank Loan Fee', doc: 'Bank Loan Fee' },
    { key: 'collateral', label: 'Collateral', doc: 'Collateral' },
    { key: 'recourse', label: 'Recourse', doc: 'Recourse' },
    { key: 'prepaymentPenalty', label: 'Prepayment Penalty', doc: 'Prepayment Penalty' },
    { key: 'minimumAnnualDscr', label: 'Minimum Annual DSCR', doc: 'Min. Annual DSCR' },
];

const FIELD_DEFAULTS = {
    creditFacilities: '60',
    purpose: 'Purchase',
    repayment: 'Monthly',
    term: '5',
    maturity: '5',
    amortization: '30',
    deposit: '5,000',
    bankLoanFee: '0.5',
    collateral: '1st Lien',
    recourse: 'Yes',
    minimumAnnualDscr: '1.35x for subject and 1.00x for global',
};

// Standard engagement language for each Summary-of-Terms row. Each entry wraps the
// value entered in the popup in the firm's fixed phrasing (from the standard Peak
// agreement); fields with no entry (asset, recourse, minimumAnnualDscr) print the
// value as typed. A template only fires when the field has a value, so an empty
// field prints blank rather than orphaned static text. `ctx` carries generate-time
// values (loanAmount, shortDate).
const stripPct = (v) => String(v == null ? '' : v).replace(/%\s*$/, '');
const SUMMARY_TEMPLATES = {
    borrower: (v) => `${v}, “TBD” LLC, and /or Assigns.`,
    creditFacilities: (v, ctx) => {
        const pct = stripPct(v);
        const amt = ctx.money(ctx.loanAmount);
        if (ctx.loanType === 'purchase') {
            // % is the LTV (pre-filled from Loan Info); loan amount is Loan Info's.
            return `Up to ${pct || '____'}% of the appraised value or purchase price, whichever is less, with an estimated loan amount of ${amt} subject to final underwriting and credit approval.`;
        }
        // Refi / Construction — the approved loan quote's amount.
        return `Up to ${pct || '____'}% of the appraised value with an estimated loan amount of ${amt} subject to final underwriting and credit approval.`;
    },
    purpose: (v) => `${v} of Commercial Real Estate & Permanent Credit Facility`,
    repayment: (v) => `${v} Principal & Interest payments (365/360) based on terms indicated below`,
    rate: (v, ctx) => `${stripPct(v) || '___'}% (As of ${ctx.shortDate})`,
    term: (v) => `${v} Years`,
    maturity: (v) => `${v} Years`,
    amortization: (v) => `${v} Years`,
    deposit: (v) => {
        const raw = String(v).replace(/^\$/, '').trim();
        const n = parseFloat(raw.replace(/,/g, ''));
        const shown = Number.isFinite(n) && /^[\d.,]+$/.test(raw) ? n.toLocaleString() : raw;
        return `$${shown} upon issuance and acceptance of a term sheet from “TBD Lender” for the purpose of third-party reports. Any unused amount will be refunded upon the closing of the loan`;
    },
    bankLoanFee: (v) => {
        const raw = String(v == null ? '' : v).trim();
        const stripped = stripPct(raw);
        // Zero or an explicit NA/N/A prints "N/A" with no "% of final loan amount".
        if (/^n\/?a$/i.test(raw) || parseFloat(stripped) === 0) return 'N/A';
        return `${stripped}% of final loan amount`;
    },
    collateral: (v, ctx) => ctx.leaseType === 'vacant'
        ? `${v} Deed of Trust Relative to Asset`
        : `${v} Deed of Trust and Assignment of Rents & Leases Relative to Asset`,
    prepaymentPenalty: (v) =>
        `${v}. Refinance prepayment penalty only, no penalty if sold to an uninterested third party.`,
};

/**
 * What the document is called, and so what it saves as from both the print
 * dialog and the Word download:
 *
 *   Peak Agreement - Wawa - North Fort Myers, FL 8.26.26
 *
 * The date is the day it was generated, which is what distinguishes one
 * printing of a property's agreement from the next.
 */
const documentTitleFor = (data = {}) => {
    const head = ['Peak Agreement', data.NAME, [data.CITY, data.STATE].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' - ');

    return [head, shortDate()].filter(Boolean).join(' ');
};

const propertyNameOf = (data = {}) => data.NAME || '';
const propertyAddressOf = (data = {}) =>
    [data.ADDRESS, [data.CITY, data.STATE].filter(Boolean).join(', '), data.ZIP]
        .filter(Boolean)
        .join(' ');

const toNumber = (value) => {
    const n = parseFloat(String(value == null ? '' : value).replace(/[%$,]/g, ''));
    return Number.isFinite(n) ? n : null;
};

/**
 * The Loan Quotes tab's Total Origination Fee, taken from the approved quote when
 * one is marked Approved = Yes and otherwise from the first quote that carries a
 * value — the popup's Bank Loan Fee reads it.
 */
const originationFeeOf = (loanQuotes) => {
    const quotes = Array.isArray(loanQuotes) ? loanQuotes.filter(Boolean) : [];
    const approved = quotes.find((q) => String(q.APPROVED).toLowerCase() === 'yes');
    const source = [approved, ...quotes].find(
        (q) => q && toNumber(q.TOTAL_ORIGINATION_FEE) != null,
    );
    return source ? toNumber(source.TOTAL_ORIGINATION_FEE) : null;
};

const emptyForm = (data = {}, loanQuotes = []) => {
    const form = { conditions: DEFAULT_CONDITIONS };
    FIELDS.forEach((f) => { form[f.key] = FIELD_DEFAULTS[f.key] || ''; });
    // Asset shows the property name and its full address, e.g.
    // "Wawa - 6522 Bayshore Rd North Fort Myers, FL 33917".
    form.asset = [propertyNameOf(data), propertyAddressOf(data)].filter(Boolean).join(' - ');

    // Pull the loan terms from the Loan Info tab when present; the fixed
    // FIELD_DEFAULTS above stand in when a field is blank. All stay editable.
    if (data.INTEREST_RATE) form.rate = String(data.INTEREST_RATE);
    if (data.TERM) form.term = String(data.TERM);
    if (data.MATURITY) form.maturity = String(data.MATURITY);
    if (data.AMORTIZATION) form.amortization = String(data.AMORTIZATION);
    if (data.PREPAYMENT_PENALTY) form.prepaymentPenalty = String(data.PREPAYMENT_PENALTY);

    // The Credit Facilities % is the LTV from the Loan Info tab.
    const ltv = toNumber(data.LTV);
    if (ltv != null) form.creditFacilities = String(ltv);

    // The Bank Loan Fee is the Loan Quotes tab's Total Origination Fee.
    const originationFee = originationFeeOf(loanQuotes);
    if (originationFee != null) form.bankLoanFee = String(originationFee);

    return form;
};

/**
 * Borrower default: the Buyer (Buyside Agent tab) for a Purchase loan, or the
 * Investor(s) (Investors tab) for a Refi / Construction loan. Both are resolved
 * to a display name asynchronously; the field stays editable. Returns '' when
 * nothing is on file.
 */
const resolveBorrower = async (data = {}) => {
    const loanType = String(data.LOAN_TYPE || '').toLowerCase();
    try {
        if (loanType === 'purchase') {
            // The Buyer, not the buyer agent. SelectBuyer reads and writes
            // INVESTORS, so BUYER_ID is an INVESTORS id despite the column name;
            // the two tables' id ranges overlap, so resolving it against BUYERS
            // returns an unrelated party rather than nothing.
            if (!data.BUYER_ID) return '';
            const res = await getData({
                tableCode: INVESTORS,
                options: {
                    column: ['ARCIMEDES_ROW_ID', 'NAME'],
                    filter: [{ code: 'ARCIMEDES_ROW_ID', text: String(data.BUYER_ID), type: 'exact' }],
                    limit: { count: 1, offset: 0 },
                },
            });
            const row = res && res.data && res.data[0];
            return row ? formatName(row) : '';
        }

        // Refi / Construction — the investor(s) on the property's current ownership.
        if (!data.ARCIMEDES_ROW_ID) return '';
        const ownRes = await executeQuery({
            queryName: GET_PROPERTY_OWNERSHIPS,
            options: { param: { PROPERTY_ID: String(data.ARCIMEDES_ROW_ID), limit: '9999', offset: '0' } },
        });
        const ownerships = ownRes && ownRes.success && Array.isArray(ownRes.data) ? ownRes.data : [];
        const current = ownerships.find((o) => o.IS_CURRENT === 1 || o.IS_CURRENT === '1') || ownerships[0];
        if (!current) return '';
        const invRes = await executeQuery({
            queryName: GET_OWNERSHIP_INVESTORS,
            options: { param: { PROPERTY_OWNERSHIP_ID: String(current.ARCIMEDES_ROW_ID), limit: '9999', offset: '0' } },
        });
        const investors = invRes && invRes.success && Array.isArray(invRes.data) ? invRes.data : [];
        return investors.map((i) => formatName(i)).filter(Boolean).join(', ');
    } catch (e) {
        return '';
    }
};

const escapeHtml = (value) =>
    String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

// --- Fixed template language (verbatim from the standard Peak agreement) ---

const INTRO =
    'Thank you for engaging Peak Capital I LLC (“Peak Capital”) to provide you with the capital markets ' +
    'services indicated below. The purpose of this Agreement is to define the scope of that representation, how our ' +
    'services will be provided to you, and the fees associated with such services. This Agreement is legally binding ' +
    'and should be read carefully.';

const SERVICES_PRE =
    'Peak Capital will assist you, the undersigned borrower (“Borrower”), in seeking financing with ' +
    'prospective lenders and in negotiating the terms of financing for the following assets referred to as ';
const SERVICES_MID = ' (the “Asset”) generally located at ';
const SERVICES_POST =
    '. To that end, Peak Capital will create and structure a financing request memorandum package and shall submit ' +
    'that package to prospective lenders in an attempt to generate competitive offers. Peak Capital will consult with ' +
    'you in connection with its preparation of the financing request memorandum package. In performing these services, ' +
    'you understand that Peak Capital is not acting as your agent or the agent of any lender and is not responsible for ' +
    'performing any investigation or due diligence of the Asset or the underlying investment, or for providing ' +
    'professional tax or legal advice. From the date the Borrower executes this Agreement until the date that is six ' +
    '(6) months thereafter (the “Term”) you are granting to Peak Capital the exclusive right and authority ' +
    'from the to work on your behalf to obtain financing on the Asset. Except with respect to the obligation of ' +
    'Borrower to pay Fee which are due to or may become due to Peak Capital, and except for obligations that ' +
    'specifically survive the expiration of this Agreement, all other obligations and responsibilities of the parties ' +
    'hereto shall terminate upon such expiration of this Agreement.';

const FEE_1 =
    'Notwithstanding the expiration of the Term above, Borrower agrees to pay Peak Capital an Origination Fee ' +
    '(“Fee”) equal to One Percent (1.0%) of the final loan amount or $10,000.00, whichever is greater upon ' +
    'the occurrence of any of the following events: (1) a commitment or proposed note or mortgage is issued on terms ' +
    'and conditions agreed to or accepted by you, regardless of whether or not the loan closes; (2) you obtain any debt ' +
    'financing on the Asset from or through a source other than Peak Capital during the Term or any extension thereof ' +
    '(unless Peak Capital is unable to provide acceptable loan terms); (3) you take any actions during the Term or any ' +
    'extension thereof (including, but not limited to, cancellation of an accepted commitment or financing, failing to ' +
    'provide information required by a lender to process or close a loan, or defaulting on a commitment or financing) ' +
    'that prohibit, prevent or otherwise interfere with the performance of Peak Capital’s services or the funding ' +
    'of debt financing; or (4) you obtain any debt financing within five (5) years of the Effective Date from any ' +
    'lender disclosed or introduced to you or any entity related to you by Peak Capital.';

const FEE_2 =
    'The Fee is due to Peak Capital upon the earlier of (a) the closing of the loan relating to the Asset, or (b) 90 ' +
    'days after the occurrence of any of items described in the preceding paragraph.  At closing, Peak Capital shall be ' +
    'entitled to make demand of any closing agent for payment from the proceeds of the financing, and you shall issue ' +
    'instructions directing the closing agent to pay Peak Capital the Fee. In no event shall Peak Capital be paid later ' +
    'than 30 days after any of the events resulting in the Fee.';

const FEE_3 =
    'The Fee is in addition to all other third-party processing and report costs, and you agree to pay any expenses ' +
    'that may be required or incurred by a lender to complete the debt financing, including, but not limited to, title ' +
    'insurance, application fees, legal fees, appraisal costs, inspection fees, commitment fees, survey costs, ' +
    'documentation fees, and environmental audits.';

const OTHER_TERMS = [
    'You understand that Peak Capital is not a lender and does not guarantee that financing can be obtained. If Peak ' +
        'Capital obtains a commitment or proposed note or mortgage, you agree to review it promptly and to assist Peak ' +
        'Capital in negotiating a final form acceptable to you and the lender, though the ultimate decision to accept ' +
        'financing remains yours.',
    'You agree to provide information requested by Peak Capital and/or prospective lenders and you represent that the ' +
        'information provided will be complete and accurate. You authorize Peak Capital and prospective lenders to ' +
        'investigate your credit and financial histories as well as those of any entity or individual related to this ' +
        'Agreement, and to verify information from any source. You agree to defend, indemnify, and hold Peak Capital ' +
        'harmless from any and all claims, demands, liabilities, and damages arising from or related to any incorrect ' +
        'information supplied by you or any material information which you fail to supply.',
    'This Agreement shall be governed by, and construed in accordance with, the laws of the State of Texas, without ' +
        'regard for any conflict of law principles that would dictate the application of the laws of another ' +
        'jurisdiction.  You and Peak Capital agree that the District Courts for Collin County, Texas shall have ' +
        'exclusive and sole jurisdiction to enforce any violation of this Agreement. You and Peak Capital consent to ' +
        'the personal jurisdiction of those courts for any dispute arising from or relating to this Agreement.  The ' +
        'parties agree and consent that service of process as provided by Texas law may be made upon the parties in ' +
        'any such suit, action, or proceeding brought in such courts and may not claim that any such suit, action, or ' +
        'proceeding has been brought in an inconvenient forum.',
    'This Agreement is binding upon and inures to the benefit of the parties and their legal representatives, ' +
        'successors and assigns.  You may not assign your rights under this Agreement without Peak Capital’s prior ' +
        'written consent.',
    'This Agreement contains the entire understanding between the parties. There are no other written or oral ' +
        'agreements between the parties relating to the subject matter of this Agreement.',
    'You understand and acknowledge that the closing of the financing contemplated under this Agreement could be ' +
        'delayed by factors outside of Peak Capital’s control. If delayed, you may realize a taxable event or ' +
        'suffer other tax and financial penalties. You hereby release and hold Peak Capital harmless from any and all ' +
        'liability or other financial consequences arising out of or related to such delay.',
    'You acknowledge that Peak Capital is relying on information provided by you and has not made an independent ' +
        'investigation, determination, analysis, or representation of, among other things, the value of the Asset, the ' +
        'financial, legal, title, physical, geological, or environmental condition of the Asset, the financial ' +
        'condition or business prospects of any tenant, or such tenant’s intent to continue or renew its tenancy ' +
        'in the Asset, or the suitability of the Asset for your contemplated use. You agree that investigation and ' +
        'analysis of the foregoing matters is your sole responsibility and that you will hold Peak Capital harmless ' +
        'from any liability therefrom.',
    'You agree that Peak Capital and its affiliates and assigns may release publicity articles or advertising ' +
        'concerning this debt financing. Information released typically will include the loan amount, property type ' +
        'and description, and city location.',
    'Peak Capital makes no representation as to the legal effect of any provision of this Agreement. If you desire ' +
        'legal, financial, or tax advice, please consult your attorney, accountant, or tax advisor.',
];

const SIG_INTRO =
    'If the Borrower is a corporation, company, trust or partnership, you represent by signing below that you are ' +
    'authorized to execute on behalf of and to bind the corporation, company, trust or partnership.';

// Served from frontend/public/img; resolved to an absolute URL at generate time
// because the print document opens in a blank window (no app origin of its own).
//
// The letterhead is cut from peak-capital-transparent.png, the full-resolution
// brand asset, rather than peakcapital.png - that one is 170x78, which the
// agreement shows at about 1.6in wide, so its anti-aliasing reads as grey.
const PEAK_LOGO_PATH = '/img/peak-capital-letterhead.png';
// Greg Holley's signature graphic. It goes on the printed form only - the Word
// file leaves the line blank to be signed.
//
// The trimmed copy of greg-signature.png: that one is on an opaque white
// ground with dead space above and below the ink, so laid over the signature
// line it painted out the rule.
const PEAK_SIGNATURE_PATH = '/img/greg-signature-transparent.png';

const FOOTER_TEXT = '14755 Preston Rd, Suite 440, Dallas, TX 75254 &nbsp;|&nbsp; (972) 910-2155';

// The size the Word file is set in, title aside. The printed form keeps its own
// sizes below - the two outputs are deliberately not the same here.
const WORD_FONT_SIZE = 11;

// The printed form's own stylesheet. The Word file is laid out by the backend
// converter from the same classes instead, so the two are kept in step by the
// class names rather than by these rules: .page-start starts a page, .term sets
// a label beside its value, .page-footer runs at the foot of every sheet.
const STYLE_BLOCK = `
    @page { size: 8.5in 11in portrait; margin: 0.6in 0.6in 0.5in 0.6in; }
    * { box-sizing: border-box; }
    body { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 10.5pt; line-height: 1.32; margin: 0; }
    table.doc { width: 100%; border-collapse: collapse; }
    table.doc > thead { display: table-header-group; }
    table.doc td { padding: 0; vertical-align: top; }
    .page-header { text-align: center; padding-bottom: 28px; }
    .page-header img { max-height: 70px; max-width: 280px; }
    .page-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8pt; color: #333; }
    h1 { text-align: center; font-size: 14pt; font-weight: bold; font-style: italic; margin: 0 0 16px; }
    h2 { text-align: center; font-size: 11.5pt; font-weight: bold; margin: 16px 0 8px; }
    h2.page-start { page-break-before: always; margin-top: 0; }
    p { text-align: justify; margin: 0 0 10px; }
    ul.terms-list { margin: 0 0 10px 0; padding-left: 22px; column-count: 2; column-gap: 36px; }
    ul.terms-list li { text-align: justify; margin: 0 0 10px; break-inside: avoid; -webkit-column-break-inside: avoid; }
    .summary .term { display: flex; gap: 8px; align-items: flex-start; margin: 0 0 10px; padding-left: 40px; }
    .summary .term .term-key { font-weight: bold; flex: 0 0 200px; }
    .summary .term .term-value { flex: 1 1 auto; }
    .blank-note { text-align: center; font-style: italic; margin: 28px 0; }
    .sig-page { page-break-before: always; padding-top: 8px; }
    .sig-block { margin: 18px 0; }
    .sig-line { margin: 6px 0; }
    .sig-heading { font-weight: bold; margin: 24px 0 6px; }
    /* Print only: the signature is taken out of the flow and set just above the
       rule it belongs to, so the rule stays visible underneath it. The Word
       file carries no signature at all. */
    .sig-signature-line { position: relative; }
    .sig-signature-img { position: absolute; left: 24px; bottom: 10px; height: 30px; }
`;

const PeakAgreementForm = ({ data = {}, loanQuotes = [], viewMode = false, deleted = false, disabled = false, currentUser = '' }) => {
    const propertyName = data.NAME || '';
    const propertyAddress = [data.ADDRESS, [data.CITY, data.STATE].filter(Boolean).join(', '), data.ZIP]
        .filter(Boolean)
        .join(' ');

    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(() => emptyForm(data, loanQuotes));

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const reset = () => {
        setForm(emptyForm(data, loanQuotes));
        setOpen(false);
    };

    // Open with fresh defaults, then fill Borrower from the Buyer / Investor(s)
    // once the async lookup returns (without clobbering a value already typed).
    const openForm = () => {
        setForm(emptyForm(data, loanQuotes));
        setOpen(true);
        resolveBorrower(data).then((name) => {
            if (name) setForm((prev) => ({ ...prev, borrower: prev.borrower || name }));
        });
    };

    // Shared by both outputs - print and Word - so the two always carry the
    // same terms and the same name. The letterhead and the address line are
    // kept apart from the document itself because each output repeats them its
    // own way: a table header group and a fixed footer in print, and Word's own
    // header and footer parts, which the converter builds from .page-header
    // and .page-footer.
    const buildDocument = () => {
        const today = new Date();
        const dateStr = today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
        // Short MM/DD/YY form used in the Rate line, e.g. "07/21/26".
        const rateDate = today.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });
        const logoUrl = window.location.origin + PEAK_LOGO_PATH;
        const signatureUrl = window.location.origin + PEAK_SIGNATURE_PATH;
        const title = documentTitleFor(data);

        // The section headings are centred by the stylesheet in print and by
        // the .center class in Word; .page-start is what starts a new page in
        // both. Emphasis is marked up rather than left to CSS for the same
        // reason - the converter reads the tags.
        // An inline margin is how a block asks for spacing other than its
        // class's own; the converter reads margin-top/margin-bottom too, so one
        // declaration moves both outputs.
        const styleAttr = (style) => (style ? ` style="${style}"` : '');
        const p = (text, style = '') => `<p${styleAttr(style)}>${escapeHtml(text)}</p>`;
        const heading = (text) => `<h2 class="center">${escapeHtml(text)}</h2>`;
        const headingBreak = (text, style = '') => `<h2 class="page-start center"${styleAttr(style)}>${escapeHtml(text)}</h2>`;

        const servicesHtml =
            `<p>${escapeHtml(SERVICES_PRE)}${escapeHtml(propertyName)}${escapeHtml(SERVICES_MID)}` +
            `${escapeHtml(propertyAddress)}${escapeHtml(SERVICES_POST)}</p>`;

        // Two columns in both outputs: the stylesheet's column-count in print,
        // and .two-column - which the converter turns into a section of its
        // own, that being how Word does columns - in the Word file.
        const otherTermsHtml =
            `<ul class="terms-list two-column">${OTHER_TERMS.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ul>`;

        const toNum = (val) => {
            const n = parseFloat(String(val == null ? '' : val).replace(/[$,]/g, ''));
            return Number.isFinite(n) ? n : null;
        };
        const money = (n) => `$${n == null ? '____' : Math.round(n).toLocaleString()}`;
        const loanType = String(data.LOAN_TYPE || '').toLowerCase();
        const leaseType = String(data.LEASE_TYPE || '').toLowerCase();
        // Estimated loan amount is pulled from the Loan Info tab's Loan Amount for
        // both loan types; only the surrounding language differs.
        const loanAmount = toNum(data.LOAN_AMOUNT);
        const ctx = { shortDate: rateDate, loanType, leaseType, loanAmount, money };

        const summaryRows = FIELDS
            .map((f) => {
                const raw = form[f.key];
                const template = SUMMARY_TEMPLATES[f.key];
                // Templates that read the entered value only fire when it's present,
                // so an empty field prints blank rather than "of final loan amount".
                const shown = template && (raw || f.key === 'creditFacilities')
                    ? template(raw, ctx)
                    : raw;
                // The label is bold in the markup as well as the stylesheet:
                // the Word converter takes its emphasis from the tags, and the
                // .term-key class is what tells it the label sits in a column
                // of its own with the term set beside it.
                return `<div class="term"><span class="term-key"><strong>${escapeHtml(f.doc)}:</strong></span><span class="term-value">${escapeHtml(shown)}</span></div>`;
            })
            .join('');

        const conditionsHtml = escapeHtml(form.conditions).replace(/\n/g, '<br/>');

        // `signature` is Greg's signature graphic on the printed form and
        // nothing at all in the Word file, which goes out to be signed.
        const bodyWith = (signature) => `
    <h1><em>Capital Services Engagement Agreement</em></h1>
    ${p(INTRO)}

    ${heading('Services to be Provided')}
    ${servicesHtml}

    ${heading('Fees for Services Provided')}
    ${p(FEE_1)}
    ${p(FEE_2)}
    ${p(FEE_3)}

    ${headingBreak('Other Terms', 'margin-bottom:20px;')}
    ${otherTermsHtml}

    ${headingBreak('Summary of Terms')}
    <br/>
    <div class="summary">
        ${summaryRows}
        <div class="term" style="margin-bottom:28px;"><span class="term-key"><strong>Conditions:</strong></span><span class="term-value">${conditionsHtml}</span></div>
    </div>

    <div class="blank-note">(The remainder of this page has been left intentionally blank. The signature page follows.)</div>

    <div class="sig-page">
        ${p(SIG_INTRO, 'margin-top:24px;')}
        <div class="sig-block">
            <div class="sig-heading">BORROWER:</div>
            <div class="sig-line" style="margin-top:32px;">By: __________________________________</div>
            <div class="sig-line" style="margin-top:6px;">Name: _______________________________</div>
            <div class="sig-line" style="margin-top:6px;">Title: ________________________________</div>
            <div class="sig-line" style="margin-top:6px;">Date (the “Effective Date”): ${escapeHtml(dateStr)}</div>
        </div>
        <div class="sig-block">
            <div class="sig-heading" style="margin-top:40px;">PEAK CAPITAL</div>
            <div class="sig-line sig-signature-line" style="margin-top:32px;">By: __________________________________${signature}</div>
            <div class="sig-line sig-name" style="padding-left:28px;margin-top:2px;">Greg Holley</div>
            <div class="sig-line sig-name" style="padding-left:28px;margin-top:2px;">Managing Partner</div>
            <div class="sig-line" style="margin-top:6px;">Date: ${escapeHtml(dateStr)}</div>
        </div>
    </div>
`;

        const header = `<div class="page-header"><img src="${logoUrl}" alt="Peak Capital" /></div>`;
        const footer = `<div class="page-footer">${FOOTER_TEXT}</div>`;
        const signatureImg = `<img class="sig-signature-img" src="${signatureUrl}" alt="Greg Holley signature" />`;

        return {
            title,
            html: `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${STYLE_BLOCK}</style>
</head>
<body>
    <table class="doc">
    <thead>
        <tr><td>
            ${header}
        </td></tr>
    </thead>
    <tbody>
        <tr><td>
${bodyWith(signatureImg)}
        </td></tr>
    </tbody>
    </table>

    ${footer}
</body>
</html>`,
            // The converter reads the document, not the print scaffolding: no
            // table wrapping the whole agreement (that would come out as a
            // bordered box), and the letterhead and address line left where it
            // can lift them into the header and footer of every page.
            wordHtml: `${header}${bodyWith('')}${footer}`,
        };
    };

    const generate = () => {
        const { html, title } = buildDocument();

        // Print via a hidden iframe so the dialog opens in place — no new tab, no
        // redirect, and the app page itself is never sent to the printer.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

        // Chrome names the saved PDF after the browser tab rather than the frame
        // it is printing, so the tab wears the document's name - the same name
        // the Word download uses - until the dialog closes.
        const previousTitle = document.title;
        const cleanup = () => {
            document.title = previousTitle;
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        iframe.onload = () => {
            const cw = iframe.contentWindow;
            cw.focus();
            cw.onafterprint = cleanup;
            cw.print();
            // Fallback cleanup in case onafterprint never fires (e.g. dialog cancelled).
            setTimeout(cleanup, 60000);
        };

        document.title = title;
        iframe.srcdoc = html;
        document.body.appendChild(iframe);

        reset();
    };

    // The logo and the signature are served from the frontend's public
    // directory, which the app container cannot reach, so they travel in the
    // request as data URIs rather than as links the server cannot follow.
    const downloadWord = async () => {
        const { wordHtml, title } = buildDocument();
        await downloadDocumentWord(await withEmbeddedImages(wordHtml), title, WORD_FONT_SIZE);
        reset();
    };

    return (
        <React.Fragment>
            <div className="Loan_column_item" style={{ minHeight: 0, margin: 0, marginBottom: '16px' }}>
                <button
                    type="button"
                    className="button-new"
                    style={{ border: '1px solid rgb(144, 185, 237)', color: 'rgb(144, 185, 237)', background: 'transparent', width: 'auto', padding: '0px 16px', margin: 0 }}
                    disabled={disabled || deleted}
                    onClick={(e) => { e.preventDefault(); openForm(); }}
                >
                    PEAK AGREEMENT
                </button>
            </div>

            <Modal className="loi-main" show={open} onClose={reset}>
                <div className="loi-modal">
                    <span className="green-title"> Peak Agreement </span>
                    <div className="close-button" onClick={reset} />
                </div>
                <div
                    className="loi-modal-content"
                    style={{ display: 'grid', gap: '4px 80px', gridTemplateColumns: '400px 400px 400px' }}
                >
                    {FIELDS.map((f) => (
                        <Input
                            key={f.key}
                            label={f.label}
                            value={form[f.key]}
                            onChange={(event) => update(f.key, event.target.value)}
                        />
                    ))}
                </div>
                <div style={{ marginTop: '12px', maxWidth: '880px' }}>
                    <label className="input-label" style={{ display: 'block', marginBottom: '4px' }}>Conditions</label>
                    <textarea
                        className="input"
                        style={{ width: '100%', minHeight: '120px', padding: '10px', fontFamily: 'inherit' }}
                        value={form.conditions}
                        onChange={(event) => update('conditions', event.target.value)}
                    />
                </div>
                <div className="modal-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', marginTop: '16px' }}>
                    <button type="button" className="button-new positive" onClick={generate}>Generate</button>
                    <button type="button" className="button-new negative" onClick={downloadWord}>Word</button>
                    <button type="button" className="button-new negative" onClick={reset}>Cancel</button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default PeakAgreementForm;
